<template>
    <div class="tla-form-group" v-if="showComponent">
    <label class="tla-form-group__label tla-form-group__label--required" v-if="!hideLabel" v-html="label"></label>
    <div class="retailer-selection">
      <div class="tla-form-group__touch-points retailer-selection__list" v-if="!isLoading">
        <touchpoint v-for="(item) in retailers"
          :key="item.retailerId"
          :collectionName="'retailer'"
          :inputId="item.retailerId"
          :inputValue="item.retailerId"
          :isSelected="false"
          :isRadioStyle="true"
          :validationScope="validationScope"
          :isFullWidth="true"
          @retailer="selectRetailer($event)">
          <div class="retailer-selection__item" role="button" slot="touchpoint">
            <span class="retailer-selection__text retailer-selection__text--uppercase">{{item.retailerName}}</span>
            <span class="retailer-selection__text">{{item.address.addressLine1}} {{item.address.postCode}}</span>
            <span class="retailer-selection__text retailer-selection__text--secondary">{{item.distanceInMiles}} Miles away</span>
          </div>
        </touchpoint>
      </div>

      <div class="retailer-selection__loader" v-else>
        <span class="retailer-selection__text">{{placeholderText}}</span>
        <loader />
      </div>
    </div>

    <span class="tla-form-group__message" :class="getValidationClass('retailer', 'message')">
      Please select an option.
    </span>
  </div>
</template>

<script lang="ts">
import { Validator } from 'vee-validate';
import {
  Component,
  Prop,
  Vue,
  Watch,
  Inject
} from 'vue-property-decorator';
import { validationService } from '@/services/ValidationService';
import IVehicle from '@/models/vehicle/interfaces/vehicle.d';
import Address from '@/models/Address';
import IRetailer from '@/models/retailer/retailer';
import LoaderComponent from '../ui/LoaderComponent.vue';
import GenericTouchPointComponent from '../ui/GenericTouchPointComponent.vue';

@Component({
  components: {
  touchpoint: GenericTouchPointComponent,
  loader: LoaderComponent
  }
  })
export default class RetailerSelectionComponent extends Vue {
  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: 'Select your preferred dealership' })
  public label!: string;

  @Prop({ default: 'Retrieving Nearest Dealerships' })
  public placeholderText!: string;

  @Prop({ default: 5 })
  public retailerLimit!: number;

  @Prop({ default: () => [] })
  public allowedManufacturerIds!: number[];

  @Prop({ default: () => [] })
  public blockedRangeIds!: number[];

  @Prop({ default: false })
  public hideLabel!: boolean;

  private isLoading: boolean = false;

  private noResults: boolean = false;

  public getValidationClass(fieldName: string, fieldType: string) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, false, false, this.validationScope);
  }

  public get currentVehicle(): IVehicle | null {
    return this.$store.getters['vehicleModule/currentVehicle'];
  }

  public get address(): Address {
    return this.$store.getters['addressModule/address'];
  }

  public get latLong(): string {
    return `${this.address.latitude}-${this.address.longitude}`;
  }

  public get currentRangeModelId(): string {
    if (this.currentVehicle) {
      return `${this.currentVehicle.rangeId}-${this.currentVehicle.modelId}`;
    }

    return '';
  }

  public get showComponent(): boolean {
    return this.currentVehicle !== null
      && this.allowedManufacturerIds.includes(this.currentVehicle.manufacturerId)
      && !this.blockedRangeIds.includes(this.currentVehicle.rangeId)
      && !this.noResults
      && this.address.latitude !== 0
      && this.address.longitude !== 0;
  }

  public get retailers(): IRetailer[] {
    return this.$store.getters['retailerModule/retailers'].slice(0, this.retailerLimit);
  }

  public selectRetailer(retailerId: string) {
    this.$store.dispatch('retailerModule/submitSelectedRetailerId', Number.parseInt(retailerId, 10));
  }

  @Watch('latLong')
  @Watch('currentRangeModelId')
  public async getRetailers() {
    this.isLoading = true;
    this.noResults = false;

    await this.$store.dispatch('retailerModule/submitResetSelection');

    await this.$store.dispatch('retailerModule/submitRetrieveRetailers', this.retailerLimit);

    if (!this.retailers || this.retailers.length === 0) {
      this.noResults = true;
    }

    this.isLoading = false;
  }

  public async created() {
    await this.getRetailers();
  }
}
</script>
